import { createContext, useCallback, useContext, useEffect, useMemo, useReducer, type ReactNode } from 'react';
import { defaultRenderImage, type RenderImage } from '../../utils/image/renderImage';
import {
  DEFAULT_INSTALLMENT_TERMS,
  MIN_FINANCED_PERCENT,
  type MenuRedirectConfig,
  type SimUnit,
  type SimuladorCtaButton,
  type VehicleModel,
  type VehicleVersion,
} from './types';
import { simpleInstallment } from './utils/finance';

export interface PersonalData {
  name: string;
  email: string;
  phone: string;
  cpf: string;
  birthdate: string;
  contactPrefs: string[];
  lgpdAccepted: boolean;
}

export interface TradeInData {
  /** labels (exibição no resumo) */
  brand: string;
  model: string;
  year: string;
  /** códigos FIPE (encadeamento das consultas) */
  brandCode: string;
  modelCode: string;
  yearCode: string; // "ano-combustivel", ex.: "1987-1"
  km: number;
  askingValue: number;
  fipeValue: number;
}

export interface SimulationData {
  downPayment: number;
  tradeInValue: number;
  term: number;
}

export interface WizardState {
  step: number;
  hasTradeIn: boolean;
  vehicle: { unitId: string; modelId: string | null; versionId: string | null; price: number };
  personal: PersonalData;
  tradeIn: TradeInData;
  simulation: SimulationData;
  submitted: boolean;
  errors: Record<string, string>;
}

export interface SimResult {
  term: number;
  installment: number;
  financed: number;
  downPayment: number;
  tradeInValue: number;
  vehiclePrice: number;
}

export interface WizardConfig {
  privacyPolicyText: string;
  privacyRedirect?: MenuRedirectConfig;
  /** @deprecated fallback de leitura para conteúdo salvo antes do redirect padrão. */
  privacyPolicyUrl: string;
  /** campo exibido = campo obrigatório */
  showCpf: boolean;
  showBirthdate: boolean;
  terms: number[];
  summaryLegalText: string;
  submitEnabled: boolean;
  ctaButton?: SimuladorCtaButton;
}

/** Normaliza os prazos configurados no sidebar (dedupe, >0, asc; fallback padrão). */
export const sanitizeTerms = (terms?: number[]): number[] => {
  const clean = Array.from(new Set(terms ?? [])).filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
  return clean.length ? clean : [...DEFAULT_INSTALLMENT_TERMS];
};

interface WizardContextValue {
  state: WizardState;
  set: (updater: (draft: WizardState) => void) => void;
  config: WizardConfig;
  vehicles: VehicleModel[];
  units: SimUnit[];
  /** injetada pelo wrapper: `<img>` (builder) ou `next/image` (renderer). */
  renderImage: RenderImage;
  /* derivados */
  model: VehicleModel | null;
  version: VehicleVersion | null;
  unit: SimUnit | null;
  entradaTotal: number;
  availableTerms: number[];
  maxDownPayment: number;
  financedAmount: number;
  result: SimResult;
  /* navegação */
  stepOrder: number[];
  goToStep: (step: number) => void;
  next: () => void;
  prev: () => void;
  /* helpers */
  minVersionPrice: (m: VehicleModel) => number;
}

const WizardContext = createContext<WizardContextValue | null>(null);

export const initialWizardState = (
  firstModel?: VehicleModel,
  defaultUnitId?: string,
  terms?: number[],
): WizardState => {
  const cleanTerms = sanitizeTerms(terms);
  return {
    step: 1,
    hasTradeIn: false,
    vehicle: {
      unitId: defaultUnitId || '',
      modelId: firstModel ? firstModel.id : null,
      versionId: firstModel && firstModel.versions[0] ? firstModel.versions[0].id : null,
      price: firstModel && firstModel.versions[0] ? firstModel.versions[0].price : 0,
    },
    personal: { name: '', email: '', phone: '', cpf: '', birthdate: '', contactPrefs: ['whatsapp'], lgpdAccepted: false },
    tradeIn: { brand: '', model: '', year: '', brandCode: '', modelCode: '', yearCode: '', km: 0, askingValue: 0, fipeValue: 0 },
    simulation: { downPayment: 0, tradeInValue: 0, term: cleanTerms[cleanTerms.length - 1] },
    submitted: false,
    errors: {},
  };
};

type Action = { type: 'set'; updater: (draft: WizardState) => void } | { type: 'reset'; state: WizardState };

const reducer = (state: WizardState, action: Action): WizardState => {
  if (action.type === 'reset') return action.state;
  const draft: WizardState = structuredClone(state);
  action.updater(draft);
  return draft;
};

const minVersionPrice = (m: VehicleModel): number =>
  m.versions.length ? Math.min(...m.versions.map((v) => v.price)) : 0;

interface ProviderProps {
  children: ReactNode;
  vehicles: VehicleModel[];
  units: SimUnit[];
  config: WizardConfig;
  initialState: WizardState;
  renderImage?: RenderImage;
}

export const WizardProvider = ({ children, vehicles, units, config, initialState, renderImage }: ProviderProps) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const set = useCallback((updater: (draft: WizardState) => void) => dispatch({ type: 'set', updater }), []);

  const model = useMemo(() => vehicles.find((v) => v.id === state.vehicle.modelId) || null, [vehicles, state.vehicle.modelId]);
  const version = useMemo(
    () => (model ? model.versions.find((vr) => vr.id === state.vehicle.versionId) || null : null),
    [model, state.vehicle.versionId],
  );
  const unit = useMemo(() => units.find((u) => u.id === state.vehicle.unitId) || null, [units, state.vehicle.unitId]);

  const price = state.vehicle.price || 0;
  const usedValue = state.hasTradeIn ? state.simulation.tradeInValue || 0 : 0;
  const entradaTotal = (state.simulation.downPayment || 0) + usedValue;
  const availableTerms = useMemo(() => sanitizeTerms(config.terms), [config.terms]);
  const maxDownPayment = Math.max(0, price - usedValue - price * MIN_FINANCED_PERCENT);
  const financedAmount = Math.max(0, price - (state.simulation.downPayment || 0) - usedValue);

  /* Se o Champion remover o prazo selecionado no sidebar, cai no último válido. */
  useEffect(() => {
    if (!availableTerms.includes(state.simulation.term)) {
      set((d) => {
        d.simulation.term = availableTerms[availableTerms.length - 1];
      });
    }
  }, [availableTerms, state.simulation.term, set]);

  const result = useMemo<SimResult>(
    () => ({
      term: state.simulation.term,
      installment: simpleInstallment(financedAmount, state.simulation.term),
      financed: financedAmount,
      downPayment: state.simulation.downPayment,
      tradeInValue: usedValue,
      vehiclePrice: price,
    }),
    [state.simulation.term, state.simulation.downPayment, financedAmount, usedValue, price],
  );

  const stepOrder = useMemo(() => (state.hasTradeIn ? [1, 2, 3, 4, 5] : [1, 2, 4, 5]), [state.hasTradeIn]);

  const goToStep = useCallback((step: number) => set((d) => { d.step = step; d.errors = {}; }), [set]);
  const next = useCallback(() => {
    const idx = stepOrder.indexOf(state.step);
    if (idx >= 0 && idx < stepOrder.length - 1) goToStep(stepOrder[idx + 1]);
  }, [stepOrder, state.step, goToStep]);
  const prev = useCallback(() => {
    const idx = stepOrder.indexOf(state.step);
    if (idx > 0) goToStep(stepOrder[idx - 1]);
  }, [stepOrder, state.step, goToStep]);

  const value: WizardContextValue = {
    state,
    set,
    config,
    vehicles,
    units,
    renderImage: renderImage ?? defaultRenderImage,
    model,
    version,
    unit,
    entradaTotal,
    availableTerms,
    maxDownPayment,
    financedAmount,
    result,
    stepOrder,
    goToStep,
    next,
    prev,
    minVersionPrice,
  };

  return <WizardContext.Provider value={value}>{children}</WizardContext.Provider>;
};

export const useWizard = (): WizardContextValue => {
  const ctx = useContext(WizardContext);
  if (!ctx) throw new Error('useWizard must be used within WizardProvider');
  return ctx;
};
